'use client';

import { useMemo } from 'react';
import type { BestLineEligibility } from '@sharpr/pricing';
import { rowPricer } from '@/lib/board/price';
import type { AccessTier } from '@/lib/access';
import type { SelectionViewResponse } from '@/types/selection';
import { useNbaLiveSeed, type NbaBoardTiming } from './useNbaLiveSeed';

const NO_OUT: ReadonlySet<string> = new Set();
// A questionable player of a projected game the viewer may mark out (a chip), with his team.
export interface QuestionablePlayer { gameId: string; playerId: string; name: string; teamAbbr: string }
const NO_QUESTIONABLE: QuestionablePlayer[] = [];

// The NBA odds screen's data (spec §4.4): the live seed, priced in the browser into the rows the screen renders.
// Returns the shape the screen's MLB query returns.
// `tier`: the viewer's NBA access tier (useAccess). `outQuestionable`: the questionable players the viewer has out
// (boardPlayerKey); an NBA game prices at the scenario they make (spec §4.4).
export function useNbaBoard({ enabled, tier, bestLineEligibility, outQuestionable = NO_OUT }: {
  enabled: boolean; tier: AccessTier; outQuestionable?: ReadonlySet<string>;
  bestLineEligibility: BestLineEligibility;   // what may win a best line (a new object re-prices every row)
}, timing?: NbaBoardTiming) {
  const { board, error, refetch } = useNbaLiveSeed({ enabled, tier }, timing);

  // Keyed by content, so a caller passing a new but equal set each render doesn't re-price the board.
  const outKey = [...outQuestionable].sort().join('\n');
  const out = useMemo<ReadonlySet<string>>(() => new Set(outKey ? outKey.split('\n') : []), [outKey]);
  const price = useMemo(() => rowPricer(), []);

  const data = useMemo<SelectionViewResponse | undefined>(() => {
    if (!board) return undefined;
    const { seed } = board;
    return {
      players: price(board, bestLineEligibility, out),
      projectionsLastUpdated: seed.lastUpdated,
      // The release-time banner shows where the viewer has no projection data (spec §13.3 releaseTime).
      projectionsHidden: ![...board.games.values()].some(g => g.projection),
      projectionsAvailableAt: seed.releaseTime,
      noSportAccess: seed.view === 'public',
    };
  }, [board, bestLineEligibility, out, price]);

  const games = board?.games;
  const questionable = useMemo(() => (games ? [...games.values()].flatMap(g => (g.projection?.questionable ?? [])
    .map(q => ({ gameId: g.gameId, playerId: q.playerId, name: q.name, teamAbbr: g.teams[q.side].abbr }))) : NO_QUESTIONABLE), [games]);
  return { data, error, refetch, questionable };
}
